import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE } from '../lib/config';
import { EyeIcon } from '@heroicons/react/24/outline';

const AdminWithdrawalsPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        // Admin権限チェック
        if (!user || user.user_type !== 'admin') {
            toast.error('管理者権限が必要です');
            navigate('/');
            return;
        }
        fetchItems();
        // eslint-disable-next-line
    }, [user, navigate, filterStatus]);

    const fetchItems = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE}/admin/withdrawal-requests?status=${filterStatus}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Admin-Token': process.env.REACT_APP_ADMIN_TOKEN || 'admin-secret'
                }
            });
            if (res.ok) {
                const data = await res.json();
                setItems(data);
            } else {
                toast.error('データの取得に失敗しました');
            }
        } catch (e) {
            console.error(e);
            toast.error('エラーが発生しました');
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (requestId, status) => {
        let msg = `ステータスを「${status}」に変更しますか？`;
        if (status === 'rejected') msg += '\n※資金はユーザーに返還されます。';
        if (!window.confirm(msg)) return;

        try {
            setProcessing(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE}/admin/withdrawal-requests/${requestId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-Admin-Token': process.env.REACT_APP_ADMIN_TOKEN || 'admin-secret'
                },
                body: JSON.stringify({ status })
            });
            if (res.ok) {
                toast.success('更新しました');
                fetchItems();
                setShowModal(false);
            } else {
                toast.error('更新に失敗しました');
            }
        } catch (e) {
            console.error(e);
            toast.error('エラーが発生しました');
        } finally {
            setProcessing(false);
        }
    };

    const openModal = (item) => {
        setSelectedRequest(item);
        setShowModal(true);
    };

    if (loading && items.length === 0) return <div className="p-8">読み込み中...</div>;

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">出金申請管理</h1>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="border rounded px-3 py-1"
                    >
                        <option value="all">全て</option>
                        <option value="pending">未対応 (Pending)</option>
                        <option value="approved">承認済 (Approved)</option>
                        <option value="paid">振込完了 (Paid)</option>
                        <option value="rejected">否認 (Rejected)</option>
                    </select>
                </div>

                <div className="bg-white rounded shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">申請ID / ユーザー</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">金額</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">銀行口座</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">申請日</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {items.map(item => (
                                <tr key={item.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{item.id.substring(0, 10)}...</div>
                                        <div className="text-sm text-gray-500">{item.user_name || item.user_email || item.user_id}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                        ¥{(item.amount || 0).toLocaleString()}
                                        <div className="text-xs text-gray-400 font-normal">手数料: ¥{item.fee}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {item.bank_info ? (
                                            <div>
                                                <div>{item.bank_info.bank_name} {item.bank_info.branch_name}</div>
                                                <div className="text-xs">{item.bank_info.account_type} {item.bank_info.account_number}</div>
                                                <div className="text-xs">{item.bank_info.account_holder_name}</div>
                                            </div>
                                        ) : <span className="text-gray-400">不明</span>}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(item.created_at).toLocaleString()}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                                            ${item.status === 'paid' ? 'bg-green-100 text-green-800' :
                                                item.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                    item.status === 'approved' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                            {item.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button onClick={() => openModal(item)} className="text-blue-600 hover:text-blue-900 flex items-center">
                                            <EyeIcon className="w-4 h-4 mr-1" /> 詳細
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">データがありません</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Detail Modal */}
                {showModal && selectedRequest && (
                    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-600 bg-opacity-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
                            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                                <h3 className="text-lg font-medium">出金申請詳細</h3>
                                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div><label className="text-xs text-gray-500">申請ID</label><div className="font-mono text-sm">{selectedRequest.id}</div></div>
                                    <div><label className="text-xs text-gray-500">ステータス</label>
                                        <div className="font-bold">{selectedRequest.status}</div>
                                    </div>
                                    <div><label className="text-xs text-gray-500">申請額</label><div className="text-xl font-bold">¥{selectedRequest.amount}</div></div>
                                    <div><label className="text-xs text-gray-500">手数料</label><div>¥{selectedRequest.fee}</div></div>
                                    <div className="col-span-2">
                                        <label className="text-xs text-gray-500">振込先口座</label>
                                        {selectedRequest.bank_info ? (
                                            <div className="bg-gray-50 p-3 rounded text-sm">
                                                <div>{selectedRequest.bank_info.bank_name} ({selectedRequest.bank_info.bank_code})</div>
                                                <div>{selectedRequest.bank_info.branch_name} ({selectedRequest.bank_info.branch_code})</div>
                                                <div>{selectedRequest.bank_info.account_type} {selectedRequest.bank_info.account_number}</div>
                                                <div className="font-bold">{selectedRequest.bank_info.account_holder_name}</div>
                                            </div>
                                        ) : <div>情報なし</div>}
                                    </div>
                                </div>

                                {selectedRequest.status === 'pending' && (
                                    <div className="flex justify-end gap-3 mt-6 border-t pt-4">
                                        <button
                                            onClick={() => handleAction(selectedRequest.id, 'rejected')}
                                            disabled={processing}
                                            className="px-4 py-2 border border-red-300 text-red-700 rounded hover:bg-red-50"
                                        >
                                            否認 (返金)
                                        </button>
                                        <button
                                            onClick={() => handleAction(selectedRequest.id, 'approved')}
                                            disabled={processing}
                                            className="px-4 py-2 border border-blue-300 text-blue-700 rounded hover:bg-blue-50"
                                        >
                                            承認のみ
                                        </button>
                                        <button
                                            onClick={() => handleAction(selectedRequest.id, 'paid')}
                                            disabled={processing}
                                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-bold"
                                        >
                                            振込完了
                                        </button>
                                    </div>
                                )}
                                {selectedRequest.status === 'approved' && (
                                    <div className="flex justify-end gap-3 mt-6 border-t pt-4">
                                        <button
                                            onClick={() => handleAction(selectedRequest.id, 'paid')}
                                            disabled={processing}
                                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-bold"
                                        >
                                            振込完了にする
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminWithdrawalsPage;
